import { useActor, useMachine } from '@xstate/react'
import * as React from 'react'
import { InterpreterFrom } from 'xstate'

import { createContext } from '~logic/lib/react'
import { productDetailMachine } from '~logic/xstate/machines/menu/productDetailMachine'

import { useLocaleCtx } from '../../../config/contexts/LocaleProvider'
import { useLoadProductDetailService } from '../hooks/useLoadProductDetailService'
import { useProductDetailCtx } from './ProductDetailProvider'

type TMachine = InterpreterFrom<typeof productDetailMachine>

const [Provider, useDetailMachineCtx] = createContext<TMachine>(`ProductDetailMachine`)

type TLoadProductProps = FirstParam<typeof useLoadProductDetailService>

type TProps = RequiredChildren & TLoadProductProps

// TODO provide the service & use actor/selectors locally to subscribe to more granular changes?

/**
 * provides machine service controlling product detail loading, state & caching
 * ! this machine should stay rendered throughout the MenuScreen lifecycle, otherwise the cache will be lost
 */
export const ProductDetailMachineProvider: React.FC<TProps> = ({
  branchId,
  warePriceType,
  children,
}) => {
  const locale = useLocaleCtx()

  const { fetchProductDetail } = useLoadProductDetailService({ branchId, warePriceType })

  const { product, promoTag, categoryId, editedCartItemIds } = useProductDetailCtx()

  const [, send, service] = useMachine(productDetailMachine, {
    services: { fetchProductDetail },
  })

  // when product detail modal is closed (product set tu null) clear the machine state
  // when product detail modal is open (product set to the selected list product) load the detail info about that product
  React.useEffect(() => {
    if (!product) {
      send({ type: 'CLEAR_PRODUCT' })

      return
    }

    send({
      type: 'SELECT_PRODUCT',
      payload: {
        promoTag,
        categoryId,
        productId: product.id,
        editedItemIds: editedCartItemIds,
      },
    })
  }, [categoryId, editedCartItemIds, product, promoTag, send])

  // clear the cache when locale changes
  React.useEffect(() => {
    send({ type: 'CLEAR_CACHE' })
  }, [locale, send])

  return <Provider value={service}>{children}</Provider>
}

export const useProductDetailMachine = () => {
  return useActor(useDetailMachineCtx())
}
